// The timing readout's arithmetic (slice 15; `?perf=1`): frame gaps into the
// numbers a person reads, and whether the browser draws in software. A
// timing taken on a software renderer measures the machine, not the table
// (design/25; the dev box and the QA agent both draw in software), so the
// readout says so rather than letting its numbers be read as findings.

export interface FrameStats {
  /** Frames per second over the window. */
  fps: number;
  /** The 95th-percentile gap between frames, in milliseconds. */
  p95: number;
  /** The longest gap, in milliseconds. */
  worst: number;
}

/** Gaps between frames, in milliseconds, over a window of `windowMs`: the numbers the readout shows. */
export function frameStats(gaps: number[], windowMs: number): FrameStats {
  if (gaps.length === 0) return { fps: 0, p95: 0, worst: 0 };
  const sorted = [...gaps].sort((a, b) => a - b);
  const span = Math.max(windowMs, 1);
  return {
    fps: Math.round((gaps.length * 1000) / span),
    p95: Math.round(sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]!),
    worst: Math.round(sorted[sorted.length - 1]!),
  };
}

export const isSoftwareRenderer = (name: string): boolean => /swiftshader|llvmpipe|softpipe|software|microsoft basic render/i.test(name);

/** The WebGL renderer this browser uses, by name, or a sentence saying there is none. */
export function rendererName(doc: Document = document): string {
  try {
    const gl = doc.createElement('canvas').getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) return 'no WebGL';
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    return String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
  } catch {
    return 'no WebGL';
  }
}

/** Whether the address asks for the readout. */
export const perfRequested = (search: string): boolean => new URLSearchParams(search).get('perf') === '1';
